import { z } from 'zod';

import { UuidSchema } from '../common/id/id';
import { makeListQuery } from '../common/list-query/list-query';

import { LegalDocTypeSchema } from './doc-type/doc-type';

/**
 * One member's acceptance of one version — append-only, never deleted
 * (`03-legal-consent.md` §consent records).
 *
 * This is the legal artifact the whole module exists to produce: who accepted
 * what text, when, from where, on which build. It outlives the account, which
 * is why `user_consents.userId` carries no foreign key.
 */
export const UserConsentSchema = z
  .object({
    id: UuidSchema,
    userId: UuidSchema,
    versionId: UuidSchema,
    documentId: UuidSchema,
    documentKey: z.string(),
    docType: LegalDocTypeSchema,
    version: z.number().int().min(1),
    /** The version's title in the active locale, so a history row reads. */
    title: z.string(),
    consentedAt: z.iso.datetime(),
    /** Recorded server-side from the request. `null` when the proxy sent none. */
    ip: z.string().nullable(),
    appVersion: z.string().nullable(),
  })
  .strict();

export type UserConsent = z.infer<typeof UserConsentSchema>;

/**
 * Plain pagination over a consent list whose subject is already fixed by the
 * route: the acceptance list of one version (`admin/legal/versions/:id/consents`
 * — the audit in `03-legal-consent.md` §admin) and a member's own history
 * (`legal/consents`, where the subject is the token).
 */
export const ConsentsPageQuerySchema = makeListQuery({});

export type ConsentsPageQuery = z.infer<typeof ConsentsPageQuerySchema>;

/**
 * One member's consent history — the staff support view.
 *
 * `userId` is **required**, not optional. An unfiltered dump of every consent
 * record ever taken is neither a useful answer nor one anybody asked for, and a
 * schema that cannot express the request is a better guarantee than an endpoint
 * that quietly returns an empty page when the filter is forgotten.
 */
export const MemberConsentsListQuerySchema = makeListQuery({
  userId: UuidSchema,
});

export type MemberConsentsListQuery = z.infer<typeof MemberConsentsListQuerySchema>;
